import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, subDays, getDay } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSend, FiChevronDown, FiChevronUp, FiUsers, FiEye, FiFileText, FiLink, FiLink2 } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

// 요일 매핑 (월=1, 화=2, ... 일=0)
const DAY_MAP = {
  '일': 0, '월': 1, '화': 2, '수': 3, '목': 4, '금': 5, '토': 6
};

export default function Lessons() {
  const navigate = useNavigate();
  useAuth();
  const [lessons, setLessons] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pointBalance, setPointBalance] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showFeedbackSection, setShowFeedbackSection] = useState(false);
  const [classStudents, setClassStudents] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLesson, setPreviewLesson] = useState(null);
  const [previewStudentId, setPreviewStudentId] = useState('all');  // 'all' = 관리자 뷰, studentId = 학부모 뷰
  // 시험 연동 관련 상태
  const [suggestedTests, setSuggestedTests] = useState([]);
  const [allClassTests, setAllClassTests] = useState([]);  // 전체 시험 (드롭다운용)
  const [linkedTestStats, setLinkedTestStats] = useState([]);
  const [linkingTest, setLinkingTest] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState('');  // 드롭다운 선택값
  const [showTemplateAverage, setShowTemplateAverage] = useState(false);  // 템플릿 평균 포함 여부
  const [formData, setFormData] = useState({
    classGroupId: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    title: '',
    content: '',
    homework: '',
    nextPlan: '',
    studentFeedbacks: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [lessonsRes, classesRes, pointsRes] = await Promise.all([
        api.get('/api/lessons'),
        api.get('/api/class-groups', { params: { active: true } }),
        api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }))
      ]);
      setLessons(lessonsRes.data.lessons || []);
      setClasses(classesRes.data.classGroups || []);
      setPointBalance(pointsRes.data.balance || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 선택된 반의 스케줄 요일 가져오기
  const selectedClass = useMemo(() => {
    return classes.find(c => c._id === formData.classGroupId);
  }, [classes, formData.classGroupId]);

  // 허용된 요일 목록 (JS day index: 0=일, 1=월, ...)
  const allowedDays = useMemo(() => {
    if (!selectedClass?.schedule?.length) return null; // null이면 모든 날짜 허용
    return selectedClass.schedule.map(s => DAY_MAP[s.day]);
  }, [selectedClass]);

  // 선택 가능한 날짜인지 체크
  const isDateAllowed = (dateStr) => {
    if (!allowedDays) return true;
    const date = new Date(dateStr);
    return allowedDays.includes(getDay(date));
  };

  // 허용된 날짜 목록 생성 (오늘 기준 ±60일)
  const allowedDates = useMemo(() => {
    if (!allowedDays) return null;
    const dates = [];
    const today = new Date();
    for (let i = -60; i <= 60; i++) {
      const date = addDays(today, i);
      if (allowedDays.includes(getDay(date))) {
        dates.push(format(date, 'yyyy-MM-dd'));
      }
    }
    return dates;
  }, [allowedDays]);

  // 반 변경 시 학생 목록 로드 및 날짜 자동 조정
  const handleClassChange = async (classGroupId) => {
    setFormData(prev => ({ ...prev, classGroupId, studentFeedbacks: [] }));
    setShowFeedbackSection(false);

    if (classGroupId) {
      try {
        const { data } = await api.get(`/api/class-groups/${classGroupId}`);
        const cls = data.classGroup;
        setClassStudents(cls.students || []);

        // 현재 선택된 날짜가 허용되지 않으면 가장 가까운 허용 날짜로 변경
        if (cls.schedule?.length) {
          const clsAllowedDays = cls.schedule.map(s => DAY_MAP[s.day]);
          const currentDate = new Date(formData.date);

          if (!clsAllowedDays.includes(getDay(currentDate))) {
            // 가장 가까운 허용 날짜 찾기
            for (let i = 0; i <= 7; i++) {
              const futureDate = addDays(currentDate, i);
              if (clsAllowedDays.includes(getDay(futureDate))) {
                setFormData(prev => ({ ...prev, date: format(futureDate, 'yyyy-MM-dd') }));
                break;
              }
              const pastDate = subDays(currentDate, i);
              if (clsAllowedDays.includes(getDay(pastDate))) {
                setFormData(prev => ({ ...prev, date: format(pastDate, 'yyyy-MM-dd') }));
                break;
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to load class students:', error);
        setClassStudents([]);
      }
    } else {
      setClassStudents([]);
    }
  };

  const openModal = async (lesson = null) => {
    if (lesson) {
      setEditingLesson(lesson);
      const classGroupId = lesson.classGroupId?._id || lesson.classGroupId;

      // 학생 목록 로드
      if (classGroupId) {
        try {
          const { data } = await api.get(`/api/class-groups/${classGroupId}`);
          setClassStudents(data.classGroup.students || []);
        } catch (error) {
          setClassStudents([]);
        }
      }

      setFormData({
        classGroupId,
        date: format(new Date(lesson.date), 'yyyy-MM-dd'),
        title: lesson.title,
        content: lesson.content || '',
        homework: lesson.homework || '',
        nextPlan: lesson.nextPlan || '',
        studentFeedbacks: lesson.studentFeedbacks?.map(sf => ({
          studentId: sf.studentId?._id || sf.studentId,
          feedback: sf.feedback || ''
        })) || []
      });
      setShowFeedbackSection(lesson.studentFeedbacks?.length > 0);
    } else {
      setEditingLesson(null);
      setClassStudents([]);
      setFormData({
        classGroupId: classes[0]?._id || '',
        date: format(new Date(), 'yyyy-MM-dd'),
        title: '',
        content: '',
        homework: '',
        nextPlan: '',
        studentFeedbacks: []
      });
      setShowFeedbackSection(false);

      // 첫 번째 반의 학생 목록 로드
      if (classes[0]?._id) {
        handleClassChange(classes[0]._id);
      }
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 날짜 유효성 검사
    if (!isDateAllowed(formData.date)) {
      alert('선택한 반의 수업 요일이 아닙니다. 수업 요일을 확인해주세요.');
      return;
    }

    // 빈 피드백 제거
    const cleanedFeedbacks = formData.studentFeedbacks.filter(sf => sf.feedback?.trim());

    try {
      const submitData = {
        ...formData,
        studentFeedbacks: cleanedFeedbacks
      };

      if (editingLesson) {
        await api.put(`/api/lessons/${editingLesson._id}`, submitData);
      } else {
        await api.post('/api/lessons', submitData);
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/api/lessons/${id}`);
      loadData();
    } catch (error) {
      alert('삭제에 실패했습니다');
    }
  };

  // 포인트 체크 함수 (수업일지 발송 시)
  const checkPointsForSend = () => {
    // 수업일지 발송은 푸시 (10P) 사용
    const requiredPoints = 10;

    if (pointBalance < requiredPoints) {
      const result = window.confirm(
        `포인트가 부족합니다.\n\n` +
        `현재 포인트: ${pointBalance.toLocaleString()}P\n` +
        `필요 포인트: ${requiredPoints}P (푸시 발송)\n\n` +
        `충전하시겠습니까?\n\n` +
        `[확인] → 포인트 충전 페이지로 이동\n` +
        `[취소] → 알림 설정 페이지로 이동`
      );

      if (result) {
        navigate('/points');
      } else {
        navigate('/settings', { state: { activeTab: 'notification' } });
      }
      return false;
    }

    return true;
  };

  const handleSend = async (id) => {
    // 포인트 체크
    if (!checkPointsForSend()) {
      return;
    }

    if (!window.confirm('학부모에게 수업 일지를 발송하시겠습니까?')) return;
    try {
      await api.post(`/api/lessons/${id}/send`);
      alert('발송되었습니다');
      // 포인트 잔액 새로고침
      const pointsRes = await api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }));
      setPointBalance(pointsRes.data.balance || 0);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '발송에 실패했습니다');
    }
  };

  // 개별 피드백 업데이트
  const updateStudentFeedback = (studentId, feedback) => {
    setFormData(prev => {
      const existing = prev.studentFeedbacks.find(sf => sf.studentId === studentId);
      if (existing) {
        return {
          ...prev,
          studentFeedbacks: prev.studentFeedbacks.map(sf =>
            sf.studentId === studentId ? { ...sf, feedback } : sf
          )
        };
      } else {
        return {
          ...prev,
          studentFeedbacks: [...prev.studentFeedbacks, { studentId, feedback }]
        };
      }
    });
  };

  // 학생 피드백 값 가져오기
  const getStudentFeedback = (studentId) => {
    return formData.studentFeedbacks.find(sf => sf.studentId === studentId)?.feedback || '';
  };

  // 미리보기 모달 열기
  const openPreviewModal = async (lesson) => {
    try {
      // 상세 정보 조회 (학생 피드백 + 시험 정보 포함)
      const { data } = await api.get(`/api/lessons/${lesson._id}`);
      setPreviewLesson(data.lesson);
      setSuggestedTests(data.suggestedTests || []);
      setAllClassTests(data.allClassTests || []);
      setLinkedTestStats(data.linkedTestStats || []);
      setSelectedTestId('');  // 드롭다운 초기화
      setPreviewStudentId('all');  // 기본은 관리자 뷰
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Failed to load lesson details:', error);
      alert('수업 일지 정보를 불러오는데 실패했습니다');
    }
  };

  // 시험 연결
  const handleLinkTest = async (testId) => {
    if (!previewLesson || !testId) return;
    setLinkingTest(true);
    try {
      await api.post(`/api/lessons/${previewLesson._id}/link-test`, {
        testId,
        showTemplateAverage
      });
      // 데이터 새로고침
      const refreshed = await api.get(`/api/lessons/${previewLesson._id}`);
      setPreviewLesson(refreshed.data.lesson);
      setSuggestedTests(refreshed.data.suggestedTests || []);
      setAllClassTests(refreshed.data.allClassTests || []);
      setLinkedTestStats(refreshed.data.linkedTestStats || []);
      setSelectedTestId('');  // 드롭다운 초기화
      setShowTemplateAverage(false);  // 체크박스 초기화
    } catch (error) {
      alert(error.response?.data?.error || '시험 연결에 실패했습니다');
    } finally {
      setLinkingTest(false);
    }
  };

  // 시험 연결 해제
  const handleUnlinkTest = async (testId) => {
    if (!previewLesson) return;
    if (!window.confirm('시험 연결을 해제하시겠습니까?')) return;
    setLinkingTest(true);
    try {
      await api.delete(`/api/lessons/${previewLesson._id}/unlink-test/${testId}`);
      // 데이터 새로고침
      const refreshed = await api.get(`/api/lessons/${previewLesson._id}`);
      setPreviewLesson(refreshed.data.lesson);
      setSuggestedTests(refreshed.data.suggestedTests || []);
      setAllClassTests(refreshed.data.allClassTests || []);
      setLinkedTestStats(refreshed.data.linkedTestStats || []);
    } catch (error) {
      alert(error.response?.data?.error || '시험 연결 해제에 실패했습니다');
    } finally {
      setLinkingTest(false);
    }
  };

  // 선택된 학생의 피드백만 가져오기
  const getFilteredFeedbacks = () => {
    if (!previewLesson?.studentFeedbacks) return [];
    if (previewStudentId === 'all') {
      return previewLesson.studentFeedbacks.filter(sf => sf.feedback);
    }
    return previewLesson.studentFeedbacks.filter(
      sf => sf.feedback && (sf.studentId?._id || sf.studentId) === previewStudentId
    );
  };

  return (
    <div className="lessons-page">
      <div className="page-header">
        <h2>수업 일지</h2>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> 일지 작성
        </button>
      </div>

      {/* Lessons List */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>날짜</th>
              <th>반</th>
              <th>제목</th>
              <th>개별피드백</th>
              <th>발송</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="loading">로딩 중...</td></tr>
            ) : lessons.length === 0 ? (
              <tr><td colSpan="6" className="empty">작성된 수업 일지가 없습니다</td></tr>
            ) : (
              lessons.map((lesson) => (
                <tr key={lesson._id}>
                  <td>{format(new Date(lesson.date), 'yyyy-MM-dd')}</td>
                  <td>{lesson.classGroupId?.name || '-'}</td>
                  <td>{lesson.title}</td>
                  <td>
                    {lesson.studentFeedbacks?.filter(sf => sf.feedback).length > 0 ? (
                      <span className="badge badge-info">
                        <FiUsers size={12} /> {lesson.studentFeedbacks.filter(sf => sf.feedback).length}명
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    <span className={`status ${lesson.sentAt ? 'active' : 'inactive'}`}>
                      {lesson.sentAt ? '발송완료' : '미발송'}
                    </span>
                  </td>
                  <td>
                    <button className="icon-btn" onClick={() => openPreviewModal(lesson)} title="보기">
                      <FiEye />
                    </button>
                    <button className="icon-btn" onClick={() => openModal(lesson)}>
                      <FiEdit2 />
                    </button>
                    {!lesson.sentAt && (
                      <button className="icon-btn" onClick={() => handleSend(lesson._id)} title="발송">
                        <FiSend />
                      </button>
                    )}
                    <button className="icon-btn danger" onClick={() => handleDelete(lesson._id)}>
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingLesson ? '수업 일지 수정' : '수업 일지 작성'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>반 *</label>
                  <select
                    value={formData.classGroupId}
                    onChange={(e) => handleClassChange(e.target.value)}
                    required
                  >
                    <option value="">선택하세요</option>
                    {classes.map((cls) => (
                      <option key={cls._id} value={cls._id}>
                        {cls.name}
                        {cls.schedule?.length > 0 && ` (${cls.schedule.map(s => s.day).join(', ')})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    날짜 *
                    {allowedDays && (
                      <span className="label-hint">
                        ({selectedClass?.schedule?.map(s => s.day).join(', ')}요일만 선택 가능)
                      </span>
                    )}
                  </label>
                  {allowedDates ? (
                    <select
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    >
                      {allowedDates.map(date => (
                        <option key={date} value={date}>{date}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>제목 *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="오늘의 수업 제목"
                  required
                />
              </div>
              <div className="form-group">
                <label>수업 내용</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="오늘 수업에서 다룬 내용을 입력하세요"
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>과제</label>
                <textarea
                  value={formData.homework}
                  onChange={(e) => setFormData({ ...formData, homework: e.target.value })}
                  placeholder="과제 내용을 입력하세요"
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>다음 수업 예고</label>
                <textarea
                  value={formData.nextPlan}
                  onChange={(e) => setFormData({ ...formData, nextPlan: e.target.value })}
                  placeholder="다음 수업에서 다룰 내용"
                  rows={2}
                />
              </div>

              {/* 개별 학생 피드백 섹션 */}
              {classStudents.length > 0 && (
                <div className="feedback-section">
                  <button
                    type="button"
                    className="feedback-toggle"
                    onClick={() => setShowFeedbackSection(!showFeedbackSection)}
                  >
                    <FiUsers />
                    개별 학생 피드백 ({formData.studentFeedbacks.filter(sf => sf.feedback).length}/{classStudents.length})
                    {showFeedbackSection ? <FiChevronUp /> : <FiChevronDown />}
                  </button>

                  {showFeedbackSection && (
                    <div className="student-feedbacks">
                      <p className="feedback-hint">선택적으로 개별 학생에게 피드백을 작성할 수 있습니다. 작성하지 않은 학생에게는 반 전체 내용만 발송됩니다.</p>
                      {classStudents.map((student) => (
                        <div key={student._id} className="student-feedback-item">
                          <label>{student.name}</label>
                          <textarea
                            value={getStudentFeedback(student._id)}
                            onChange={(e) => updateStudentFeedback(student._id, e.target.value)}
                            placeholder={`${student.name} 학생에 대한 개별 피드백 (선택)`}
                            rows={2}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingLesson ? '수정' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewLesson && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>수업 일지 미리보기</h3>
              <button className="close-btn" onClick={() => setShowPreviewModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body lesson-preview">
              {/* 뷰 선택기 */}
              <div className="preview-view-selector">
                <label>미리보기 모드:</label>
                <select
                  value={previewStudentId}
                  onChange={(e) => setPreviewStudentId(e.target.value)}
                >
                  <option value="all">관리자 뷰 (전체 피드백 보기)</option>
                  {previewLesson.studentFeedbacks?.filter(sf => sf.feedback).map((sf) => (
                    <option key={sf.studentId?._id || sf.studentId} value={sf.studentId?._id || sf.studentId}>
                      학부모 뷰 - {sf.studentId?.name || '학생'}
                    </option>
                  ))}
                </select>
                {previewStudentId !== 'all' && (
                  <span className="view-hint">※ 학부모에게는 해당 자녀의 피드백만 표시됩니다</span>
                )}
              </div>

              <div className="preview-header">
                <div className="preview-meta">
                  <span className="preview-class">{previewLesson.classGroupId?.name}</span>
                  <span className="preview-date">{format(new Date(previewLesson.date), 'yyyy년 MM월 dd일')}</span>
                  {previewLesson.sentAt && (
                    <span className="preview-sent">
                      발송일: {format(new Date(previewLesson.sentAt), 'yyyy-MM-dd HH:mm')}
                    </span>
                  )}
                </div>
                <h4 className="preview-title">{previewLesson.title}</h4>
              </div>

              {previewLesson.content && (
                <div className="preview-section">
                  <h5>📚 수업 내용</h5>
                  <p>{previewLesson.content}</p>
                </div>
              )}

              {previewLesson.homework && (
                <div className="preview-section">
                  <h5>📝 과제</h5>
                  <p>{previewLesson.homework}</p>
                </div>
              )}

              {previewLesson.nextPlan && (
                <div className="preview-section">
                  <h5>📅 다음 수업 예고</h5>
                  <p>{previewLesson.nextPlan}</p>
                </div>
              )}

              {/* 연결된 시험 결과 */}
              {linkedTestStats.length > 0 && (
                <div className="preview-section test-section">
                  <h5><FiFileText /> 연결된 시험 결과</h5>
                  <div className="linked-tests">
                    {linkedTestStats.map((stat) => {
                      // 학부모 뷰: 자기 아이 점수만 필터링
                      const filteredScores = previewStudentId === 'all'
                        ? stat.studentScores
                        : stat.studentScores?.filter(ss => ss.studentId === previewStudentId);
                      const myChildScore = filteredScores?.[0];

                      return (
                        <div key={stat.testId} className="linked-test-card">
                          <div className="test-card-header">
                            <span className="test-name">{stat.testName}</span>
                            {/* 관리자 뷰에서만 연결 해제 버튼 표시 */}
                            {previewStudentId === 'all' && (
                              <button
                                className="btn-unlink"
                                onClick={() => handleUnlinkTest(stat.testId)}
                                disabled={linkingTest}
                                title="연결 해제"
                              >
                                <FiLink2 />
                              </button>
                            )}
                          </div>
                          <div className="test-stats">
                            <div className="stat-item">
                              <span className="stat-label">응시</span>
                              <span className="stat-value">{stat.resultCount}명</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">평균</span>
                              <span className="stat-value highlight">{stat.avgScore}점</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">최고</span>
                              <span className="stat-value">{stat.maxScore}점</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">최저</span>
                              <span className="stat-value">{stat.minScore}점</span>
                            </div>
                          </div>
                          {stat.subject && <span className="test-subject">{stat.subject}</span>}

                          {/* 학부모 뷰: 우리 아이 점수만 표시 */}
                          {previewStudentId !== 'all' && myChildScore && (
                            <div className="student-scores" style={{ marginTop: '12px', borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
                              <h6 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '8px' }}>
                                우리 아이 점수 (만점: {stat.totalScore}점)
                              </h6>
                              <div style={{
                                padding: '10px 12px',
                                backgroundColor: '#F9FAFB',
                                borderRadius: '6px',
                                fontSize: '0.85rem'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: '#374151' }}>{myChildScore.studentName}</span>
                                  <span style={{
                                    fontWeight: 'bold',
                                    color: myChildScore.score >= stat.avgScore ? '#059669' : '#DC2626'
                                  }}>
                                    {myChildScore.score}점
                                  </span>
                                </div>
                                {myChildScore.feedback && (
                                  <div style={{
                                    marginTop: '6px',
                                    padding: '6px 8px',
                                    backgroundColor: '#E0F2FE',
                                    borderRadius: '4px',
                                    color: '#0369A1',
                                    fontSize: '0.8rem'
                                  }}>
                                    💬 {myChildScore.feedback}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* 학부모 뷰: 아이 점수가 없을 때 */}
                          {previewStudentId !== 'all' && !myChildScore && (
                            <div style={{ marginTop: '12px', color: '#6B7280', fontSize: '0.85rem' }}>
                              ※ 아직 시험 결과가 입력되지 않았습니다.
                            </div>
                          )}

                          {/* 관리자 뷰: 전체 학생 점수 표시 */}
                          {previewStudentId === 'all' && stat.studentScores?.length > 0 && (
                            <div className="student-scores" style={{ marginTop: '12px', borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
                              <h6 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '8px' }}>
                                <FiUsers size={12} style={{ marginRight: '4px' }} />
                                학생별 점수 (만점: {stat.totalScore}점)
                              </h6>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {stat.studentScores.map((ss) => (
                                  <div
                                    key={ss.studentId}
                                    style={{
                                      padding: '10px 12px',
                                      backgroundColor: '#F9FAFB',
                                      borderRadius: '6px',
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span style={{ color: '#374151' }}>{ss.studentName}</span>
                                      <span style={{
                                        fontWeight: 'bold',
                                        color: ss.score >= stat.avgScore ? '#059669' : '#DC2626'
                                      }}>
                                        {ss.score}점
                                      </span>
                                    </div>
                                    {ss.feedback && (
                                      <div style={{
                                        marginTop: '6px',
                                        padding: '6px 8px',
                                        backgroundColor: '#E0F2FE',
                                        borderRadius: '4px',
                                        color: '#0369A1',
                                        fontSize: '0.8rem'
                                      }}>
                                        💬 {ss.feedback}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 연결 가능한 시험 (추천) */}
              {suggestedTests.length > 0 && (
                <div className="preview-section suggested-tests-section">
                  <h5><FiLink /> 연결 가능한 시험</h5>
                  <p className="section-hint">같은 날짜, 같은 반의 시험입니다. 연결하면 수업일지에 시험 결과가 포함됩니다.</p>
                  <div className="suggested-tests">
                    {suggestedTests.map((test) => (
                      <div key={test._id} className="suggested-test-item">
                        <div className="test-info">
                          <span className="test-name">{test.name}</span>
                          {test.subject && <span className="test-subject">{test.subject}</span>}
                        </div>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleLinkTest(test._id)}
                          disabled={linkingTest}
                        >
                          <FiLink /> 연결
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 전체 시험에서 선택 (드롭다운) */}
              {allClassTests.length > 0 && (
                <div className="preview-section test-select-section">
                  <h5><FiFileText /> 전체 시험에서 선택</h5>
                  <p className="section-hint">같은 반의 모든 시험입니다. 템플릿 시험도 연결할 수 있습니다.</p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      value={selectedTestId}
                      onChange={(e) => setSelectedTestId(e.target.value)}
                      style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                      disabled={linkingTest}
                    >
                      <option value="">-- 시험 선택 --</option>
                      {allClassTests.map((test) => (
                        <option key={test._id} value={test._id}>
                          {test.name}
                          {test.subject ? ` (${test.subject})` : ''}
                          {test.isTemplate ? ' [템플릿]' : ''}
                          {test.testDate ? ` - ${new Date(test.testDate).toLocaleDateString()}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleLinkTest(selectedTestId)}
                      disabled={!selectedTestId || linkingTest}
                    >
                      <FiLink /> 연결
                    </button>
                  </div>
                  {/* 템플릿 평균 포함 옵션 */}
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>
                      <input
                        type="checkbox"
                        checked={showTemplateAverage}
                        onChange={(e) => setShowTemplateAverage(e.target.checked)}
                        disabled={linkingTest}
                      />
                      <span>📊 템플릿 그룹 평균 포함</span>
                      <span style={{ color: '#666', fontSize: '0.85rem' }}>(복제된 시험인 경우 같은 템플릿의 전체 평균을 포함합니다)</span>
                    </label>
                  </div>
                </div>
              )}

              {getFilteredFeedbacks().length > 0 && (
                <div className="preview-section">
                  <h5>👤 {previewStudentId === 'all' ? '개별 학생 피드백' : '우리 아이 피드백'}</h5>
                  <div className="feedback-list">
                    {getFilteredFeedbacks().map((sf, idx) => (
                      <div key={idx} className="feedback-item">
                        {previewStudentId === 'all' && <strong>{sf.studentId?.name || '학생'}</strong>}
                        <p>{sf.feedback}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {previewStudentId !== 'all' && getFilteredFeedbacks().length === 0 && (
                <div className="preview-section">
                  <p className="text-muted">해당 학생에 대한 개별 피드백이 없습니다. 공통 수업 내용만 발송됩니다.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
